import React, { useEffect, useState } from "react";
import { FaRegClock, FaDollarSign, FaBan, FaCalendarWeek } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./Timesheet_summary.css";

const fmtHrs = (n) => {
    const v = Number(n) || 0;
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

// week_start (Monday, YYYY-MM-DD) -> "29 Jun – 05 Jul, 2026"
const weekLabel = (ymd) => {
    if (!ymd) return "-";
    const [y, m, d] = ymd.split("-").map(Number);
    const start = new Date(y, m - 1, d);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const s = start.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    const e = end.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    return `${s} – ${e}, ${end.getFullYear()}`;
};

const Timesheet_summary = ({ navSize }) => {
    const [weeks, setWeeks] = useState([]);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const res = await apiFetch(`/api/timesheet/summary`, { credentials: "include" });
                const data = await res.json();
                setWeeks(Array.isArray(data.weeks) ? data.weeks : []);
                setProjects(Array.isArray(data.projects) ? data.projects : []);
            } catch (err) {
                console.error("Error fetching timesheet summary:", err);
            }
        };
        fetchSummary();
    }, []);

    const totalHours = weeks.reduce((a, w) => a + Number(w.total_hours || 0), 0);
    const billableHours = weeks.reduce((a, w) => a + Number(w.billable_hours || 0), 0);
    const nonBillable = totalHours - billableHours;

    const tiles = [
        { icon: <FaRegClock />, label: "Total Hours Logged", value: fmtHrs(totalHours), cls: "dz-blue" },
        { icon: <FaDollarSign />, label: "Billable Hours", value: fmtHrs(billableHours), cls: "dz-green" },
        { icon: <FaBan />, label: "Non-Billable Hours", value: fmtHrs(nonBillable), cls: "dz-amber" },
        { icon: <FaCalendarWeek />, label: "Weeks Logged", value: weeks.length, cls: "dz-indigo" },
    ];

    return (
        <div className="tss-container">
            <div className={`tss-page ${navSize}`}>
                <div className="header-wrapper">
                    <h2 className="dz-page-title">
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dTime Logo" className="page-logo" />
                        </a>
                        Timesheet Summary
                    </h2>
                </div>

                <p className="tss-sub">Your logged hours across weeks and projects.</p>

                <div className="dz-tile-grid tss-tiles">
                    {tiles.map((t, i) => (
                        <div key={i} className={`dz-tile ${t.cls}`}>
                            <div className="tss-tile-top">
                                <span className="tss-tile-icon">{t.icon}</span>
                                <span className="dz-tile-label">{t.label}</span>
                            </div>
                            <div className="dz-tile-value">{t.value}</div>
                        </div>
                    ))}
                </div>

                <div className="tss-columns">
                    {/* Weekly breakdown */}
                    <div className="dz-card tss-card">
                        <div className="tss-card-head"><h3>Weekly Breakdown</h3></div>
                        <div className="dz-table-wrap">
                            <table className="dz-table">
                                <thead>
                                    <tr><th>Week</th><th>Total</th><th>Billable</th><th>Non-Billable</th></tr>
                                </thead>
                                <tbody>
                                    {weeks.length === 0 ? (
                                        <tr><td colSpan={4} className="dz-table-empty">No timesheets logged yet.</td></tr>
                                    ) : weeks.map((w) => {
                                        const nb = Number(w.total_hours || 0) - Number(w.billable_hours || 0);
                                        return (
                                            <tr key={w.week_start}>
                                                <td>{weekLabel(w.week_start)}</td>
                                                <td><strong>{fmtHrs(w.total_hours)}</strong></td>
                                                <td>{fmtHrs(w.billable_hours)}</td>
                                                <td>{fmtHrs(nb)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Project breakdown */}
                    <div className="dz-card tss-card">
                        <div className="tss-card-head"><h3>By Project</h3></div>
                        <div className="dz-table-wrap">
                            <table className="dz-table">
                                <thead>
                                    <tr><th>Project</th><th>Hours</th></tr>
                                </thead>
                                <tbody>
                                    {projects.length === 0 ? (
                                        <tr><td colSpan={2} className="dz-table-empty">No project hours yet.</td></tr>
                                    ) : projects.map((p) => (
                                        <tr key={p.project_id}>
                                            <td>
                                                <span className="dz-pill dz-pill-id">{p.project_id}</span>
                                                {p.project_name ? <span className="tss-proj-name">{p.project_name}</span> : null}
                                            </td>
                                            <td><strong>{fmtHrs(p.total_hours)}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Timesheet_summary;
